/**
 * FRONT-1114: a free-text person-name input that suggests existing PERSON
 * entities from the knowledge graph as the user types — steering "Matze"
 * onto the "Matthias Müller" that's already there instead of creating a
 * second, misspelled entity. Built for the create-project dialog's People
 * rows (`create-plan-dialog.tsx`) but deliberately generic, same
 * "built for this dialog but reusable" framing as `container-picker.tsx`.
 *
 * Deliberately NOT a combobox that forces a choice from the list — entering a
 * brand-new person who isn't in the graph yet is the common case, and the
 * field must always accept free text and never auto-replace what the user
 * typed. Only an explicit pick (click, or Enter on the highlighted row)
 * replaces the field's value, with the matched entity's canonical name.
 *
 * Follows `command-palette.tsx`'s debounce precedent (a MIN length + a
 * debounce constant, and a pure `isPersonSuggestQueryPending` helper driving
 * the "Looking…" vs. hidden decision during the debounce window) and
 * `container-picker.tsx`'s Popover-based floating-list precedent — but
 * anchors via `PopoverAnchor` (not `PopoverTrigger`) so the list purely
 * follows the input without ever stealing keyboard focus from it, and the
 * list's open/closed state is driven by typed content, not a click.
 *
 * Openness is gated on the field having FOCUS, on top of the current value +
 * the settled query result. The focus gate is load-bearing, not cosmetic:
 * this component sits downstream of FRONT-1122's auto-fill, so the dialog
 * routinely opens with several People rows already carrying names long
 * enough to query. Without it, every one of those rows would open its own
 * floating list the instant the dialog appeared — a stack of overlapping
 * popovers over a form nobody has touched yet. With it, the useful half of
 * that behaviour survives intact: focusing a pre-filled row still surfaces a
 * likely existing match immediately, without waiting for a keystroke.
 */

import { useEffect, useId, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { CircleNotchIcon, UserCircleIcon } from '@phosphor-icons/react';
import { Popover, PopoverAnchor, PopoverContent } from '~/components/ui/popover';
import { Input } from '~/components/ui/input';
import { cn } from '~/lib/utils';
import { useGraphEntities, type GraphEntity } from '~/hooks/queries/use-graph';

// Suggestions only fire once the typed text reaches this length, after a
// short debounce — a single character against every person in the graph
// would be too noisy and too eager a round trip. Mirrors
// MIN_CONTENT_QUERY_LENGTH / CONTENT_SEARCH_DEBOUNCE_MS in command-palette.tsx.
const MIN_PERSON_QUERY_LENGTH = 2;
const PERSON_SUGGEST_DEBOUNCE_MS = 250;

/**
 * How many rows the floating list will ever render. The backing query returns
 * a whole page (a substring match against every person in the graph — "an"
 * alone can hit dozens), and this list has no room to grow: it hangs off a
 * form row inside a dialog. Capping the RENDERED set is what keeps the list
 * reachable — the keyboard walk and the `max-h`/scroll below both operate on
 * this same capped array, so the highlight can never point past what is
 * on screen.
 */
const MAX_PERSON_SUGGESTIONS = 8;

/**
 * Pure: true while the immediate value is long enough to trigger a
 * suggestions query but the debounced value hasn't caught up with it yet —
 * drives the "Looking…" vs. "nothing to show yet" decision during the
 * debounce window. Mirrors `isContentSearchPending` in command-palette.tsx.
 * Exported for unit testing.
 */
export function isPersonSuggestQueryPending(value: string, debouncedValue: string): boolean {
  return value.trim().length >= MIN_PERSON_QUERY_LENGTH && debouncedValue !== value;
}

/**
 * Pure: whether the floating suggestion list has anything worth showing,
 * given the matches a settled query returned. Hides once there are no
 * matches, and once the only match's name is already an exact match for what
 * the user typed — there's nothing left to suggest at that point (typing the
 * exact name of the one person who already has it isn't a duplicate risk).
 * Exported for unit testing.
 */
export function shouldShowPersonSuggestions(value: string, matches: Array<{ name: string }>): boolean {
  const trimmed = value.trim();
  if (trimmed.length < MIN_PERSON_QUERY_LENGTH) return false;
  if (matches.length === 0) return false;
  if (matches.length === 1 && matches[0].name.trim() === trimmed) return false;
  return true;
}

/**
 * Pure: the next highlighted row index for an Up/Down key press, wrapping
 * around both ends — Down from the last row (or from nothing highlighted)
 * goes to the first; Up from the first (or from nothing highlighted) goes to
 * the last. Returns -1 (nothing highlighted) when there's nothing to
 * highlight. Exported for unit testing.
 */
export function moveHighlightIndex(current: number, direction: 'down' | 'up', count: number): number {
  if (count === 0) return -1;
  if (direction === 'down') return current < count - 1 ? current + 1 : 0;
  return current > 0 ? current - 1 : count - 1;
}

/**
 * Pure: the rows the list will actually render, capped. The backing query
 * returns a whole page, and the keyboard walk must operate on exactly what is
 * on screen — so this is the ONE place the cap is applied, and the component
 * derives both the rendered rows and `matches.length` from its result.
 * Exported for unit testing.
 */
export function capPersonSuggestions<T>(items: T[]): T[] {
  return items.slice(0, MAX_PERSON_SUGGESTIONS);
}

/**
 * Pure: should a keypress be allowed to move or take the highlight?
 *
 * No while the list is closed, and no while a query is in flight — during the
 * debounce window the rows are replaced by the "Looking…" spinner, but the
 * previous result set is still in memory, so acting on a key there means
 * walking (or with Enter, PICKING) a row the user cannot see. Escape stays
 * live throughout: dismissing a list whose spinner you can see is reasonable.
 * Exported for unit testing.
 */
export function shouldActOnSuggestionKey(args: {
  open: boolean;
  loading: boolean;
  key: string;
}): boolean {
  if (!args.open) return false;
  if (args.loading) return args.key === 'Escape';
  return true;
}

/**
 * Pure: the final open/closed decision for the floating list, given every
 * input that feeds it. Extracted so the focus gate — the one rule whose
 * absence produced a screenful of overlapping lists on a freshly-opened,
 * auto-filled dialog — is directly testable rather than only observable in a
 * browser. Exported for unit testing.
 */
export function shouldOpenPersonSuggestions(args: {
  focused: boolean;
  dismissed: boolean;
  value: string;
  loading: boolean;
  hasSuggestions: boolean;
}): boolean {
  if (!args.focused || args.dismissed) return false;
  if (args.value.trim().length < MIN_PERSON_QUERY_LENGTH) return false;
  return args.loading || args.hasSuggestions;
}

export interface PersonNameInputProps {
  value: string;
  onChange: (value: string) => void;
  /** The input's accessible name — the dialog's People rows use "Person name". */
  label: string;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  className?: string;
}

export function PersonNameInput({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  maxLength,
  className,
}: PersonNameInputProps) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [dismissed, setDismissed] = useState(false);
  const [focused, setFocused] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(value), PERSON_SUGGEST_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value]);

  const debouncedTrimmed = debouncedValue.trim();
  const queryEnabled = debouncedTrimmed.length >= MIN_PERSON_QUERY_LENGTH;
  const entities = useGraphEntities('person', queryEnabled ? debouncedTrimmed : undefined, undefined, queryEnabled);
  const matches: GraphEntity[] = queryEnabled ? capPersonSuggestions(entities.items) : [];

  // Reset the highlight whenever a fresh result set lands, so a stale index
  // never survives onto a different list of rows.
  useEffect(() => {
    setHighlightIndex(-1);
  }, [entities.items]);

  const pending = isPersonSuggestQueryPending(value, debouncedValue);
  const loading = pending || (queryEnabled && entities.isLoading);
  const hasSuggestions = shouldShowPersonSuggestions(value, matches);
  const open = shouldOpenPersonSuggestions({ focused, dismissed, value, loading, hasSuggestions });

  function selectSuggestion(entity: GraphEntity) {
    // `maxLength` only constrains TYPING — a programmatic set sails past it.
    // The stored name has no length ceiling of its own (an agent write can
    // make one arbitrarily long), so without this the commit would be
    // rejected for a name the user never typed and cannot see the end of.
    onChange(maxLength ? entity.name.slice(0, maxLength) : entity.name);
    setDismissed(true);
    setHighlightIndex(-1);
    inputRef.current?.focus();
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    onChange(e.target.value);
    setDismissed(false);
    // Typing invalidates the highlight immediately. Resetting only when a
    // fresh result set lands is not enough: during the debounce window the
    // query key hasn't changed, so `entities.items` keeps its identity and
    // that effect never fires — while the list is replaced by the "Looking…"
    // spinner. A highlight surviving into that window is a pick the user can
    // no longer see, and Enter would take it.
    setHighlightIndex(-1);
  }

  function handleFocus() {
    // Arriving in the field clears a previous Escape — otherwise dismissing
    // once would silence suggestions for that row for the rest of the
    // dialog's life, including after the user comes back and edits it.
    setFocused(true);
    setDismissed(false);
  }

  function handleBlur() {
    // Leaving the field (Tab or a click elsewhere) closes the list. A click
    // ON a suggestion row never reaches here — each row's `onMouseDown`
    // prevents the browser from ever moving focus off the input in the
    // first place, so the click's `onSelect` fires with focus intact.
    setFocused(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!shouldActOnSuggestionKey({ open, loading, key: e.key })) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => moveHighlightIndex(i, 'down', matches.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => moveHighlightIndex(i, 'up', matches.length));
    } else if (e.key === 'Enter') {
      if (highlightIndex >= 0 && matches[highlightIndex]) {
        e.preventDefault();
        selectSuggestion(matches[highlightIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDismissed(true);
    }
    // Tab (and every other key) falls through untouched.
  }

  const activeOptionId = highlightIndex >= 0 ? `${listboxId}-option-${highlightIndex}` : undefined;
  // The listbox element only exists while the list is open AND settled — the
  // loading state renders a status row instead. Pointing `aria-controls` at
  // an id that isn't in the document is a dangling reference on a combobox,
  // so it is emitted only when there is something to point at.
  const listboxRendered = open && !loading;

  // Keep the keyboard-highlighted row inside the scroll box. Without this the
  // highlight walks off the bottom of the capped, scrollable list with
  // nothing to show the user where it went.
  useEffect(() => {
    if (!listboxRendered || highlightIndex < 0) return;
    document
      .getElementById(`${listboxId}-option-${highlightIndex}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, listboxRendered, listboxId]);

  return (
    <Popover open={open}>
      <PopoverAnchor asChild>
        <Input
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxRendered ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          aria-label={label}
          disabled={disabled}
          maxLength={maxLength}
          className={className}
          autoComplete="off"
        />
      </PopoverAnchor>
      {/* Gated on `open` here (not just via the Popover's own `open` prop) so
       *  this never has to render the Radix portal machinery at all in the
       *  closed state — same reason `container-picker.tsx` had to be
       *  stubbed out of `create-plan-dialog.test.tsx` entirely rather than
       *  relying on Radix's own visibility handling. */}
      {open && (
        <PopoverContent
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          // Match the field the list hangs off rather than a fixed width, so
          // it reads as an extension of that input instead of a stray panel.
          className="w-[var(--radix-popover-trigger-width)] min-w-48 p-1"
        >
          {loading ? (
            <div
              role="status"
              className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground"
            >
              <CircleNotchIcon size={12} className="animate-spin" /> Looking…
            </div>
          ) : (
            /* A listbox must own its options directly — an intervening list
             * item makes them unowned, and a screen reader then reads a plain
             * list instead of a listbox, losing the highlighted-row
             * announcement `aria-activedescendant` exists to give. So the row
             * IS the option: no button in between (which would also put every
             * row in the tab order, which this keyboard pattern forbids).
             * Capped height + scroll because the list can hold more rows than
             * fit above the dialog's edge. */
            <ul
              id={listboxId}
              role="listbox"
              aria-label={label}
              className="flex max-h-64 flex-col gap-0.5 overflow-y-auto"
            >
              {matches.map((entity, i) => (
                <li
                  key={entity.id}
                  id={`${listboxId}-option-${i}`}
                  role="option"
                  aria-selected={i === highlightIndex}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectSuggestion(entity)}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                    i === highlightIndex ? 'bg-muted text-foreground' : 'text-foreground hover:bg-muted/50',
                  )}
                >
                  <UserCircleIcon size={14} className="shrink-0 text-muted-foreground" />
                  <span className="truncate">{entity.name}</span>
                </li>
              ))}
            </ul>
          )}
        </PopoverContent>
      )}
    </Popover>
  );
}
